import { ChipsContainer } from 'app/ComponentLibrary/Filter/ChipsContainer';
import React, { createRef, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import _, { cloneDeep } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Collapse, Grid } from '@mui/material';
import { SearchPageIcons } from 'app/ComponentLibrary/Filter/SearchPageIcons';
import { makeStyles } from 'tss-react/mui';
import { selectFilters } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.selectors';
import { calculateParentPath, findBottomNode, injectItem } from '@AMIEWEB/Common/TreeView/TreeViewHelpers';
import { determineChipSelected, generateFieldChips } from '@AMIEWEB/AmWorkDesk/PlacementsDesk/filterHelper';
import { PlacementPayPackagesFilterName } from './filterUtils';

const useStyles = makeStyles()({
  fillterCollapse: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
});

const PREFIX: Record<string, string> = {
  [PlacementPayPackagesFilterName.archiveStatus]: 'Archive',
  [PlacementPayPackagesFilterName.candidateId]: 'Candidate ID',
  [PlacementPayPackagesFilterName.candidateName]: 'Candidate',
  [PlacementPayPackagesFilterName.orderId]: 'Order ID',
  [PlacementPayPackagesFilterName.placementStatus]: 'Placement Status',
  [PlacementPayPackagesFilterName.payPackageStatus]: 'Package Status',
};

export const PlacementPayPackagesChips = props => {
  const { classes } = useStyles();
  const [chips, setChips] = useState<Record<string, any>>({});
  const [showChipsCollapseBtn, setShowChipsCollapseBtn] = useState(false);
  const hiddenSubmitButton = createRef<HTMLButtonElement>();
  const { setValue, getValues } = useFormContext();
  const { t } = useTranslation();
  const filterObject = useSelector(selectFilters);
  const [checked, setChecked] = React.useState(false);
  const divRef = useRef<HTMLDivElement>(null);

  const handleChange = () => setChecked(prev => !prev);

  const getNewSelection = (key, chip) => ({
    ...filterObject?.selectedChipFilters,
    [key]: determineChipSelected(
      chip,
      filterObject?.selectedChipFilters ? filterObject?.selectedChipFilters[key] : undefined,
    ),
  });

  const handleChipDelete = (key, chip) => {
    const fieldValues = getValues(key) ?? getValues(chip.value);
    let newFieldValues: any = null;

    if (key === PlacementPayPackagesFilterName.placementStatus) {
      newFieldValues = injectItem(
        _.cloneDeep(fieldValues),
        calculateParentPath(chip.value),
        findBottomNode(chip.value),
        true,
        'value',
        'name',
      );
    } else {
      newFieldValues = Array.isArray(fieldValues)
        ? fieldValues.filter(item => item.value !== chip.value.value ?? chip?.value)
        : null;
    }

    setValue(key, newFieldValues, { shouldDirty: true });

    if (chip.isSelected || props.disableFormSubmit) {
      const newSelections = getNewSelection(key, chip);
      props.onApplyFilter(
        {
          ...getValues(),
          [key]: Array.isArray(fieldValues) ? [...(newFieldValues || [])] : null,
        },
        newSelections,
      );
    } else {
      hiddenSubmitButton.current?.click();
    }
  };

  const handleChipClick = (key, chip) => {
    if (!Array.isArray(getValues(key))) return;
    const newSelections = getNewSelection(key, chip);
    // rebuild chips based on new selection without mutating form state
    const next = buildChips(getValues(), newSelections);
    setChips(next);
    props.onApplyFilter(getValues(), newSelections);
  };

  const buildChips = (filters, selectedChipFilters) => {
    return Object.fromEntries(
      Object.entries(filters || {}).map(([key, value]) => [
        key,
        generateFieldChips(
          value,
          selectedChipFilters ? selectedChipFilters[key] : undefined,
          PREFIX[key] ?? key,
          false,
          undefined,
          false,
        ),
      ]),
    );
  };

  const computedChips = useMemo(() => {
    return filterObject?.filters ? buildChips(filterObject.filters, filterObject.selectedChipFilters) : {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterObject?.filters, filterObject?.selectedChipFilters]);

  useEffect(() => {
    setChips(computedChips);
  }, [computedChips]);

  useEffect(() => {
    const hasAny = Object.keys(chips).some(key => {
      const value = chips[key];
      if (Array.isArray(value) && value.length > 0) return true;
      if (typeof value === 'object' && value !== null && Object.keys(value).length > 0) return true;
      return false;
    });
    setShowChipsCollapseBtn(hasAny);
  }, [chips]);

  return (
    <>
      {showChipsCollapseBtn ? (
        <Grid container spacing={2}>
          <Grid item xs={11}>
            <Collapse in={checked} collapsedSize={38}>
              <div ref={divRef}>
                <ChipsContainer
                  chips={chips}
                  onDelete={(field, chip) => handleChipDelete(field, chip)}
                  onClick={(field, chip) => handleChipClick(field, chip)}
                  isCollapse={!checked}
                />
              </div>
              <button ref={hiddenSubmitButton} style={{ display: 'none' }} type="submit" />
            </Collapse>
          </Grid>
          <Grid item xs={1} className={classes.fillterCollapse}>
            {(divRef?.current?.clientHeight ?? 0) > 38 && (
              <SearchPageIcons KeyboardArrowIcon onAdvancedChipsClick={() => handleChange()} checked={checked} />
            )}
          </Grid>
        </Grid>
      ) : (
        <>
          <ChipsContainer
            chips={chips}
            onDelete={(field, chip) => handleChipDelete(field, chip)}
            onClick={(field, chip) => handleChipClick(field, chip)}
          />
          <button ref={hiddenSubmitButton} style={{ display: 'none' }} type="submit" />
        </>
      )}
    </>
  );
};

